/*
 * IMPORTS
 */
import React from "react";
import PropTypes from "prop-types";
import Moment from "moment";
import Stringg from "string";
import _ from "underscore";
import { getCode as getCountryCode } from "country-list";
import {
  HiArrowUturnDown,
  HiCodeBracketSquare,
  HiPrinter,
  HiServer,
  HiUser,
} from "react-icons/hi2";
import ReactJsonView from "react-json-view";
import { useQuery, useMutation, NetworkStatus } from "@apollo/client";
import { DataGrid } from "@mui/x-data-grid";
import { Chip } from "@mui/material";
import { toast } from "react-hot-toast";
import { connect } from "react-redux";
import "flag-icons/css/flag-icons.min.css";

import {
  Box,
  Button,
  Flex,
  Text,
  useBreakpointValue,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  IconButton,
} from "@chakra-ui/react";

/*
 * PACKAGES
 */
import DateRangePicker from "components/DateRangePicker";
import TableSpinner from "components/TableSpinner";
import { MemoizedInput, MemoizedSelect } from "components/MemoizedInput";
import Modal from "components/Modal";
import EmailReportModal from "components/EmailReport";

/*
 * GRAPHS
 */
import SmsReadQuery from "./__query__/index.sms.read.query";
import ReportCreateMutation from "./__mutation__/index.report.create.mutation";

/*
 * OBJECTS
 */

const getDlrStatusColor = (status) =>
  Object.React.App.colors.SMS_STATUS[String(status ?? "").toUpperCase()];

const Index = ({ vendorId, passOn, dispatch, activeCustomer }) => {
  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: Object.DateRangeProvider()?.startDate,
      endDate: Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 30,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filter, setFilter] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () =>
      _.debounce((nextFilterModel) => {
        const _safeFilterModel = {
          ...nextFilterModel,
          items: _.compact(nextFilterModel?.items)?.filter((i) => i?.field),
        };
        setFilter(_safeFilterModel);
      }, 800),
    [],
  );
  React.useEffect(
    () => () => _setFilterDebounced.cancel(),
    [_setFilterDebounced],
  );

  // Local state for pending filter inputs
  const [_pendingMobileNumberSearch, _setPendingMobileNumberSearch] =
    React.useState("");
  const [_pendingSmsIdToSearch, _setPendingSmsIdToSearch] = React.useState("");
  const [_pendingVendorNameToSearch, _setPendingVendorNameToSearch] =
    React.useState("");
  const [_pendingCompanyToSearch, _setPendingCompanyToSearch] =
    React.useState("");
  const [_pendingVendorSmsIdToSearch, _setPendingVendorSmsIdToSearch] =
    React.useState("");
  const [_pendingDlrStatusToSearch, _setPendingDlrStatusToSearch] =
    React.useState(void 0);
  const [_pendingSenderIdToSearch, _setPendingSenderIdToSearch] =
    React.useState("");
  const [_pendingCountryNameToSearch, _setPendingCountryNameToSearch] =
    React.useState(void 0);
  const [
    _pendingCustomerAccountSmppNameToSearch,
    _setPendingCustomerAccountSmppNameToSearch,
  ] = React.useState(void 0);
  const [_pendingMncToSearch, _setPendingMncToSearch] = React.useState(void 0);
  const [_pendingMccToSearch, _setPendingMccToSearch] = React.useState(void 0);

  const [pduToShow, setPduToShow] = React.useState("{}");

  const [_isQueryReady, _setIsQueryReady] = React.useState(
    !passOn?._navigatedToMessage,
  );

  // This state will hold the filters that are currently applied to the query. It will only be updated when the user clicks on the "Apply Filters" button.
  // This allows us to have pending filter values that the user can change without immediately
  //  affecting the query, and only apply them when they are ready.
  const [appliedFilters, setAppliedFilters] = React.useState({
    mobileNumberSearch: "",
    smsIdToSearch: "",
    vendorNameToSearch: "",
    companyToSearch: "",
    vendorSmsIdToSearch: "",
    dlrStatusToSearch: void 0,
    senderIdToSearch: "",
    countryNameToSearch: void 0,
    customerAccountSmppNameToSearch: void 0,
    mncToSearch: void 0,
    mccToSearch: void 0,
    startDate: Object.DateRangeProvider()?.startDate,
    endDate: Object.DateRangeProvider()?.endDate,
  });

  const {
    isOpen: isReactJsonViewOpen,
    onOpen: onReactJsonViewOpen,
    onClose: onReactJsonViewClose,
  } = useDisclosure();
  const {
    isOpen: isEmailReportModalOpen,
    onOpen: onEmailReportModalOpen,
    onClose: onEmailReportModalClose,
  } = useDisclosure();

  // Hook assignment.
  const [MutationReportCreate, MutationReportCreateResponse] =
    useMutation(ReportCreateMutation);

  // Data assignment.
  const _isFilterApplied = React.useMemo(
    () => filter?.items?.some((i) => i.value != void 0 && i.value !== ""),
    [filter],
  );
  const _appliedFilter = React.useMemo(
    () => _isFilterApplied ? filter : void 0,
    [_isFilterApplied, filter],
  );
  const _smsReadVariables = React.useMemo(
    () => ({
      skip: paginationModel.pageSize * paginationModel.page,
      take: paginationModel.pageSize,
      vendorId: vendorId,
      mobileNumberToSearch: appliedFilters.mobileNumberSearch,
      companyToSearch: appliedFilters.companyToSearch,
      vendorSmsIdToSearch: appliedFilters.vendorSmsIdToSearch,
      smsIdToSearch: appliedFilters.smsIdToSearch,
      dlrStatusToSearch: appliedFilters.dlrStatusToSearch,
      vendorNameToSearch: appliedFilters.vendorNameToSearch,
      senderIdToSearch: appliedFilters.senderIdToSearch,
      countryNameToSearch: appliedFilters.countryNameToSearch,
      mccToSearch: appliedFilters.mccToSearch,
      mncToSearch: appliedFilters.mncToSearch,
      customerAccountSmppNameToSearch:
        appliedFilters.customerAccountSmppNameToSearch,
      startDate: appliedFilters.startDate,
      endDate: appliedFilters.endDate,
      filter: _appliedFilter,
    }),
    [
      appliedFilters,
      _appliedFilter,
      paginationModel.page,
      paginationModel.pageSize,
      vendorId,
    ],
  );

  const _QuerySmsRead = useQuery(SmsReadQuery, {
    skip: !_isQueryReady,
    notifyOnNetworkStatusChange: true,
    variables: _smsReadVariables,
  });
  const _isLoading =
    [
      NetworkStatus.loading,
      NetworkStatus.refetch,
      NetworkStatus.setVariables,
    ].includes(_QuerySmsRead.networkStatus) || _QuerySmsRead.loading;

  const _tableHeaderHeightRef = React.useRef(0);
  const _rowCountRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: true,
    sm: true,
    md: false,
  });
  // Input change handlers for pending states
  const _onMobileNumberSearchInputChange = React.useCallback((e) =>
    _setPendingMobileNumberSearch(e.target.value), []);
  const _onVendorSmsIdToSearchInputChange = React.useCallback((e) =>
    _setPendingVendorSmsIdToSearch(e.target.value), []);
  const _oncompanyToSearchInputChange = React.useCallback((e) =>
    _setPendingCompanyToSearch(e.target.value), []);
  const _onSmsIdToSearchInputChange = React.useCallback((e) =>
    _setPendingSmsIdToSearch(e.target.value), []);
  const _onDlrStatusToSearchInputChange = React.useCallback((e) =>
    _setPendingDlrStatusToSearch(e.target.value || void 0), []);
  const _onSenderIdToSearchInputChange = React.useCallback((e) =>
    _setPendingSenderIdToSearch(e.target.value), []);
  const _onVendorNameToSearchInputChange = React.useCallback((e) =>
    _setPendingVendorNameToSearch(e.target.value), []);
  const _onMccToSearchInputChange = React.useCallback((e) =>
    _setPendingMccToSearch(e.target.value), []);
  const _onMncToSearchInputChange = React.useCallback((e) =>
    _setPendingMncToSearch(e.target.value), []);
  const _onCountryNameToSearchInputChange = React.useCallback((e) =>
    _setPendingCountryNameToSearch(e.target.value), []);
  const _onCustomerNameToSearchInputChange = React.useCallback((e) =>
    _setPendingCustomerAccountSmppNameToSearch(e.target.value), []);

  const rowCount = React.useMemo(
    () =>
      _QuerySmsRead?.data?.SmsRead
        ? _.pluck(_QuerySmsRead.data.SmsRead, "_totalCount")?.[0] ?? 0
        : _rowCountRef.current,
    [_QuerySmsRead?.data?.SmsRead],
  );
  React.useEffect(() => {
    _rowCountRef.current = rowCount;
  }, [rowCount]);

  // Apply filters handler
  const _handleApplyFilters = React.useCallback(() => {
    const newFilters = {
      mobileNumberSearch: _pendingMobileNumberSearch || void 0,
      smsIdToSearch: _pendingSmsIdToSearch || void 0,
      vendorNameToSearch: _pendingVendorNameToSearch || void 0,
      companyToSearch: _pendingCompanyToSearch || void 0,
      vendorSmsIdToSearch: _pendingVendorSmsIdToSearch || void 0,
      dlrStatusToSearch: _pendingDlrStatusToSearch || void 0,
      senderIdToSearch: _pendingSenderIdToSearch || void 0,
      countryNameToSearch: _pendingCountryNameToSearch || void 0,
      customerAccountSmppNameToSearch:
        _pendingCustomerAccountSmppNameToSearch || void 0,
      mncToSearch: _pendingMncToSearch || void 0,
      mccToSearch: _pendingMccToSearch || void 0,
      startDate: dateRange?.selection.startDate,
      endDate: dateRange?.selection.endDate,
    };

    const _isSameFilterSearch = _.isEqual(appliedFilters, newFilters);

    setAppliedFilters(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    if (_isSameFilterSearch && 0 === paginationModel.page && _isQueryReady) {
      _QuerySmsRead.refetch({
        ..._smsReadVariables,
        skip: 0,
        mobileNumberToSearch: newFilters.mobileNumberSearch,
        companyToSearch: newFilters.companyToSearch,
        vendorSmsIdToSearch: newFilters.vendorSmsIdToSearch,
        smsIdToSearch: newFilters.smsIdToSearch,
        dlrStatusToSearch: newFilters.dlrStatusToSearch,
        vendorNameToSearch: newFilters.vendorNameToSearch,
        senderIdToSearch: newFilters.senderIdToSearch,
        countryNameToSearch: newFilters.countryNameToSearch,
        mccToSearch: newFilters.mccToSearch,
        mncToSearch: newFilters.mncToSearch,
        customerAccountSmppNameToSearch:
          newFilters.customerAccountSmppNameToSearch,
        startDate: newFilters.startDate,
        endDate: newFilters.endDate,
      });
    }
  }, [
    _QuerySmsRead,
    _isQueryReady,
    _pendingCompanyToSearch,
    _pendingCountryNameToSearch,
    _pendingCustomerAccountSmppNameToSearch,
    _pendingDlrStatusToSearch,
    _pendingMccToSearch,
    _pendingMncToSearch,
    _pendingMobileNumberSearch,
    _pendingSenderIdToSearch,
    _pendingSmsIdToSearch,
    _pendingVendorNameToSearch,
    _pendingVendorSmsIdToSearch,
    _smsReadVariables,
    appliedFilters,
    dateRange?.selection.endDate,
    dateRange?.selection.startDate,
    paginationModel.page,
  ]);

  const _handlePaginationModelChange = React.useCallback((newModel) => {
    if (_isLoading) return;

    setPaginationModel(newModel);
  }, [_isLoading]);

  React.useEffect(() => {
    const _cameFromNavigation = passOn?._navigatedToMessage === true;
    const _navigationStartDate =
      passOn?.startDate ?? dateRange?.selection.startDate;
    const _navigationEndDate = passOn?.endDate ?? dateRange?.selection.endDate;

    // If navigated from another report with intent to apply filters on message report, then set pending
    // filter states and appliedFilters state to trigger query with filters.
    if (_cameFromNavigation) {
      passOn?.countryName && _setPendingCountryNameToSearch(passOn.countryName);
      passOn?.companyName && _setPendingCompanyToSearch(passOn.companyName);
      passOn?.customerAccountSmppName &&
        _setPendingCustomerAccountSmppNameToSearch(
          passOn.customerAccountSmppName,
        );
      passOn?.vendorName && _setPendingVendorNameToSearch(passOn.vendorName);
      setDateRange({
        selection: {
          startDate: _navigationStartDate,
          endDate: _navigationEndDate,
          key: "selection",
        },
      });
      passOn?.error &&
        setFilter({
          items: [
            {
              id: "error-code-navigation",
              field: "error",
              operator: "contains",
              value: passOn.error,
            },
          ],
          logicOperator: "and",
          quickFilterValues: [],
          quickFilterLogicOperator: "and",
        });

      setAppliedFilters((prev) => ({
        ...prev,
        ...(passOn?.countryName && { countryNameToSearch: passOn.countryName }),
        ...(passOn?.companyName && { companyToSearch: passOn.companyName }),
        ...(passOn?.customerAccountSmppName && {
          customerAccountSmppNameToSearch: passOn.customerAccountSmppName,
        }),
        ...(passOn?.vendorName && { vendorNameToSearch: passOn.vendorName }),
        startDate: _navigationStartDate,
        endDate: _navigationEndDate,
      }));
    }

    //passOn cleanup
    dispatch({
      type: "PASS_ON_UPDATE",
      PassOn: {
        customerId: passOn?.customerId || activeCustomer?.customerId,
        currency: passOn?.currency,
        companyName: void 0,
        countryName: void 0,
        customerAccountSmppName: void 0,
        vendorName: void 0,
        error: void 0,
        startDate: void 0,
        endDate: void 0,
        _navigatedToMessage: void 0, // Reset navigation intent flag to prevent stale filter auto-fill on direct page open
      },
    });

    _setIsQueryReady(true); //set query ready to true on component mount.
  }, []);

  // Download handler function
  const _handleDownloadReport = React.useCallback((email) => {
    const { skip, take, ...reportFilters } = _smsReadVariables;

    MutationReportCreate({
      variables: {
        startDate: _smsReadVariables.startDate,
        endDate: _smsReadVariables.endDate,
        tableName: "sms",
        email: email,
        filters: reportFilters,
      },
    })
      .then((k) => {
        toast(k.data.ReportCreate.message);
        onEmailReportModalClose(); // Close modal on success
      })
      .catch(() => {
        toast("Something went wrong please try after some time.");
      });
  }, [
    MutationReportCreate,
    _smsReadVariables,
    onEmailReportModalClose,
  ]);
  const _handleVendorPduOpen = React.useCallback((row) => {
    const vendorPduData = {
      vendor_deliver_sm: row?.vendor_deliver_sm,
      vendor_deliver_sm_resp: row?.vendor_deliver_sm_resp,
      vendor_submit_sm: row?.vendor_submit_sm,
      vendor_submit_sm_resp: row?.vendor_submit_sm_resp,
    };
    const cleanVendorPduData = Object.fromEntries(
      Object.entries(vendorPduData).filter(([, v]) => v !== null),
    );
    setPduToShow(JSON.stringify(cleanVendorPduData, null, 2));
    onReactJsonViewOpen();
  }, [onReactJsonViewOpen]);
  const _handleCustomerPduOpen = React.useCallback((row) => {
    const customerPduData = {
      customer_deliver_sm: row?.customer_deliver_sm,
      customer_submit_sm: row?.customer_submit_sm,
    };
    const cleanCustomerPduData = Object.fromEntries(
      Object.entries(customerPduData).filter(([, v]) => v !== null),
    );
    setPduToShow(JSON.stringify(cleanCustomerPduData, null, 2));
    onReactJsonViewOpen();
  }, [onReactJsonViewOpen]);
  const _handleColumnVisibilityModelChange = React.useCallback((newModel) =>
    setColumnVisibilityModel(newModel), []);

  // Columns definition
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        width: 80,
        align: "center",
        headerAlign: "center",
        sortable: false,
        disableColumnMenu: true,
        filterable: false,
      },
      {
        field: "persisted__customer.Company.displayName",
        headerName: "Company Name",
        flex: 1,
        minWidth: 250,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
        filterable: true,
      },
      {
        field: "persisted__customersmpp.displayName",
        headerName: "Smpp Account",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
      },
      {
        field: "destinationAddress",
        headerName: "Phone Number",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
      },
      {
        field: "persisted__hlrlookup.Mnc.countryName",
        headerName: "Country Name",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        filterable: true,
        editable: false,
        sortable: true,
        renderCell: ({ value }) => {
          const countryName = value ?? "-";
          const countryCode =
            countryName === "-" ? null : getCountryCode(countryName);

          return (
            <Flex align="center" justify="center" gap={2} width="100%">
              <Text as="span">{countryName}</Text>
              {countryCode && (
                <span
                  aria-label={`${countryName} flag`}
                  className={`fi fi-${countryCode.toLowerCase()}`}
                />
              )}
            </Flex>
          );
        },
      },
      {
        field: "persisted__hlrlookup.Mnc.Mcc.mcc",
        headerName: "Mcc",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
      },
      {
        field: "persisted__hlrlookup.Mnc.mnc",
        headerName: "Mnc",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        filterable: true,
        editable: false,
        sortable: true,
      },
      {
        field: "persisted__hlrlookup.Mnc.network",
        headerName: "Operator",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        filterable: true,
        editable: false,
        sortable: true,
      },
      {
        field: "status",
        headerName: "Dlr Status",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        filterable: true,
        editable: false,
        sortable: true,
        renderCell: ({ value }) => (
          <Chip
            label={value ?? "-"}
            size="small"
            sx={{
              backgroundColor: `${getDlrStatusColor(value)} !important`,
              color: "white !important",
              fontWeight: 400,
              fontSize: "6px",
            }}
          />
        ),
      },
      {
        field: "persisted__vendorsmpp.displayName",
        headerName: "Vendor Name",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        filterable: true,
        editable: false,
        sortable: true,
      },
      {
        field: "sourceAddress",
        headerName: "Sender ID",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        sortable: false,
      },
      {
        field: "transformedSourceAddress",
        headerName: "Transformed Sender ID",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        filterable: true,
        editable: false,
        sortable: true,
      },
      {
        field: "persisted__customerrateplan.rate",
        headerName: "Client Rate",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
      },
      {
        field: "clientCost",
        headerName: "Client Cost",
        width: 120,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
      },
      {
        field: "acceptedDate",
        headerName: "Accepted Date",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Moment(value).utc().format("YYYY-MM-DD hh:mm:ss A") : "-",
        editable: false,
        sortable: true,
      },
      {
        field: "submitDate",
        headerName: "Submit Date",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Moment(value).utc().format("YYYY-MM-DD hh:mm:ss A") : "-",
        editable: false,
        sortable: true,
      },
      {
        field: "dlrDate",
        headerName: "Dlr Date",
        flex: 1,
        minWidth: 200,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) =>
          value ? Moment(value).utc().format("YYYY-MM-DD hh:mm:ss A") : "-",
        editable: false,
        sortable: true,
      },
      {
        field: "latency",
        headerName: "Latency",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        valueFormatter: (value) => {
          if (!value) return "-";
          const d = Moment.duration(value);
          const parts = [];
          if (d.days() > 0) parts.push(`${d.days()}d`);
          if (d.hours() > 0) parts.push(`${d.hours()}h`);
          if (d.minutes() > 0) parts.push(`${d.minutes()}m`);
          if (d.seconds() > 0) parts.push(`${d.seconds()}s`);
          return parts.join(" ") || "0s";
        },
        editable: false,
        sortable: true,
      },
      {
        field: "characterCount",
        headerName: "Character Count",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
      },
      {
        field: "messageChunkSize",
        headerName: "Parts",
        type: "number",
        width: 100,
        align: "center",
        headerAlign: "center",
        filterable: true,
        editable: false,
        sortable: true,
      },
      {
        field: "persisted__vendorrateplan.rate",
        headerName: "Termination Rate",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        filterable: true,
        editable: false,
        sortable: true,
      },
      {
        field: "terminationCost",
        headerName: "Termination Cost",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
      },
      {
        field: "error",
        headerName: "Error Code",
        flex: 1,
        minWidth: 250,
        align: "center",
        headerAlign: "center",
        filterable: true,
        editable: false,
        sortable: true,
      },
      {
        field: "smsId",
        headerName: "Sms Id",
        flex: 1,
        minWidth: 250,
        align: "center",
        headerAlign: "center",
        filterable: false,
        editable: false,
        sortable: true,
      },
      {
        field: "receiptId",
        headerName: "Vendor Sms Id",
        width: 250,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
      },
      {
        field: "dlr",
        headerName: "Response Body",
        width: 250,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
      },
      {
        field: "originalCopyOfMessage",
        headerName: "Original Message",
        width: 300,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
      },
      {
        field: "message",
        headerName: "Transformed Message",
        width: 300,
        align: "center",
        headerAlign: "center",
        filterable: true,
        editable: false,
        sortable: true,
      },
      {
        field: "pdu",
        headerName: "PDU",
        flex: 1,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        editable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Menu placement="bottom-end" offset={[-30, -40]}>
            <MenuButton
              as={IconButton}
              disabled={false}
              icon={<HiCodeBracketSquare size={24} />}
              variant="ghost"
              size="sm"
            />
            <MenuList minW="100px" maxW="110px" py={1} bg="gray.50">
              <MenuItem
                height="30px"
                icon={<HiServer size={18} />}
                isDisabled={false}
                onClick={() => _handleVendorPduOpen(row)}
              >
                Vendor
              </MenuItem>
              <MenuDivider my="0.1" borderColor="gray.300" />
              <MenuItem
                height="30px"
                icon={<HiUser size={18} />}
                isDisabled={false}
                onClick={() => _handleCustomerPduOpen(row)}
              >
                Customer
              </MenuItem>
            </MenuList>
          </Menu>
        ),
      },
    ],
    [_handleCustomerPduOpen, _handleVendorPduOpen],
  );

  // Rows data
  const rows = React.useMemo(() => {
    if (!_QuerySmsRead?.data?.SmsRead) {
      return [];
    }
    // Convert nested server data into flat row objects for DataGrid
    const _mappedSmsRows = _QuerySmsRead.data.SmsRead.map((item, index) => {
      const _error = [];

      if (item && item?.dlr) {
        const _dlrError = Stringg(item?.dlr ?? "").between(" err:", "text")?.s;
        !_dlrError?.includes("001") && _error.push(_dlrError);
      }
      if (item && item?.error) {
        const _uniqueErrors = _.uniq(
          item.error.split(",").map((e) => e.trim()),
        );
        _error.push(..._uniqueErrors);
      }

      return {
        id:
          item?.id ??
          `message-report-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
        sno: `${index + paginationModel.pageSize * paginationModel.page + 1}.`,
        "persisted__customer.Company.displayName":
          item?.persisted__customer?.Company?.displayName ??
          "Might be bind hit.",
        "persisted__customersmpp.displayName": ["RETAIL", "RESELLER"]?.includes(
          item?.persisted__customer?.type,
        )
          ? "System Smpp"
          : (item?.persisted__customersmpp?.displayName ?? "-"),
        destinationAddress: item?.destinationAddress ?? "-",
        "persisted__hlrlookup.Mnc.countryName":
          item?.persisted__hlrlookup?.Mnc?.countryName ?? "-",
        "persisted__hlrlookup.Mnc.Mcc.mcc":
          item?.persisted__hlrlookup?.Mnc?.Mcc?.mcc ?? "-",
        "persisted__hlrlookup.Mnc.mnc":
          item?.persisted__hlrlookup?.Mnc?.mnc ?? "-",
        "persisted__hlrlookup.Mnc.network":
          item?.persisted__hlrlookup?.Mnc?.network ?? "-",
        status: item?.status ?? "-",
        "persisted__vendorsmpp.displayName": item?.isFakeDlr
          ? "P_SUNNY_DAY"
          : (item?.persisted__vendorsmpp?.displayName ?? "-"),
        sourceAddress: item?.sourceAddress ?? "-",
        transformedSourceAddress: item?.transformedSourceAddress ?? "-",
        "persisted__customerrateplan.rate":
          item?.persisted__customerrateplan?.rate ?? "-",
        clientCost: item?.persisted__customerrateplan?.rate ?? "-",
        acceptedDate: item?.createdAt ? new Date(item.createdAt) : null,
        submitDate: item?.submittedAt ? new Date(item.submittedAt) : null,
        dlrDate:
          item?.submittedAt && item?.updatedAt && item?.dlr
            ? item?.updatedAt
            : null,
        latency:
          item?.submittedAt && item?.updatedAt && item?.dlr && item?.createdAt
            ? new Date(item.updatedAt).getTime() -
              new Date(item.createdAt).getTime()
            : void 0,
        characterCount: item?.messageLength ?? "-",
        messageChunkSize: item?.messageChunkSize ?? "-",
        "persisted__vendorrateplan.rate":
          item?.persisted__vendorrateplan?.rate ?? "-",
        terminationCost: item?.persisted__vendorrateplan?.rate ?? "-",
        error:
          0 === _error?.length
            ? "-"
            : _.uniq(_.without(_error, "NONE"))?.join(", "),
        smsId: item?.id ?? "-",
        receiptId: item?.receiptId ?? "-",
        dlr: item?.dlr ?? "-",
        originalCopyOfMessage: item?.originalCopyOfMessage ?? "-",
        message: item?.message ?? "-",
        vendor_deliver_sm: item?.vendor_deliver_sm,
        vendor_deliver_sm_resp: item?.vendor_deliver_sm_resp,
        vendor_submit_sm: item?.vendor_submit_sm,
        vendor_submit_sm_resp: item?.vendor_submit_sm_resp,
        customer_deliver_sm: item?.customer_deliver_sm,
        customer_submit_sm: item?.customer_submit_sm,
      };
    });
    return _mappedSmsRows;
  }, [
    _QuerySmsRead?.data?.SmsRead,
    paginationModel.page,
    paginationModel.pageSize,
  ]);

  // Return component.
  return (
    <>
      <Flex
        className="reportsMessage base"
        height="auto"
        minH="max-content"
        overflow="visible"
      >
        <Flex
          display="flex"
          flex="0 0 auto"
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="calc(100vh - 20px)"
          height="auto"
          overflow="visible"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex pb="0px" justify="space-between" alignItems="center">
            <Flex flex={0.6} gap="6px" flexDir="column">
              <Text
                as="h3"
                fontSize={17}
                fontWeight={500}
                display="flex"
                alignItems="center"
                color="brand.500"
                gap="6px"
              >
                <HiArrowUturnDown />
                {_isCurrentViewMobile
                  ? "Filter Rows"
                  : "Filter Rows based on Date."}
              </Text>
              <DateRangePicker
                startDate={dateRange?.selection?.startDate}
                endDate={dateRange?.selection?.endDate}
                w="100%"
                fontSize="13px"
                height="max-content"
                gap="6px"
                setSelectDate={setDateRange}
              />
            </Flex>
            <Button
              borderRadius="12px"
              maxW="250px"
              backgroundColor="purple.200"
              color="purple"
              aria-label="Export data in table into csv."
              onClick={onEmailReportModalOpen}
              _hover={{ bg: "purple.800", color: "white" }}
            >
              {_isCurrentViewMobile ? (
                void 0
              ) : (
                <Text as="h4" mr={2} fontWeight="500">
                  Download Sheet
                </Text>
              )}
              <HiPrinter size="20px" />
            </Button>
          </Flex>

          {/*  Filter */}
          <Flex
            gap="12px"
            flexWrap="wrap"
            bg="white"
            p="16px"
            borderRadius="12px"
            border="1px solid #C5CFE8"
            alignItems="flex-end"
          >
            <Box flex="1" minW="200px">
              <Text
                mb={2}
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
              >
                Company Name
              </Text>
              <MemoizedInput
                placeholder='e.g. "C_Comapany"'
                className="filter searchInput"
                value={_pendingCompanyToSearch}
                onChange={_oncompanyToSearchInputChange}
              />
            </Box>
            <Box flex="1" minW="200px">
              <Text
                mb={2}
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
              >
                Smpp Account
              </Text>
              <MemoizedInput
                placeholder='e.g. "C_SMPP"'
                className="filter searchInput"
                defaultValue={_pendingCustomerAccountSmppNameToSearch}
                onChange={_onCustomerNameToSearchInputChange}
              />
            </Box>
            <Box flex="1" minW="200px">
              <Text
                mb={2}
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
              >
                Phone Number
              </Text>
              <MemoizedInput
                placeholder='e.g. "923001234567"'
                className="filter searchInput"
                defaultValue={_pendingMobileNumberSearch}
                onChange={_onMobileNumberSearchInputChange}
              />
            </Box>

            <Box flex="1" minW="200px">
              <Text
                mb={2}
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
              >
                Country Name
              </Text>
              <MemoizedInput
                placeholder='e.g. "France"'
                className="filter searchInput"
                value={_pendingCountryNameToSearch}
                onChange={_onCountryNameToSearchInputChange}
              />
            </Box>

            <Box flex="1" minW="150px">
              <Text
                mb={2}
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
              >
                MCC
              </Text>
              <MemoizedInput
                placeholder='e.g. "402"'
                className="filter searchInput"
                defaultValue={_pendingMccToSearch}
                onChange={_onMccToSearchInputChange}
              />
            </Box>

            <Box flex="1" minW="150px">
              <Text
                mb={2}
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
              >
                MNC
              </Text>
              <MemoizedInput
                placeholder='e.g. "02"'
                className="filter searchInput"
                defaultValue={_pendingMncToSearch}
                onChange={_onMncToSearchInputChange}
              />
            </Box>
            <Box
              flex={{ base: "0 0 100%", md: "1" }}
              w={{ base: "100%", md: "100%" }}
              minW="250px"
            >
              <Text
                mb={2}
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
              >
                {" "}
                DLR Status
              </Text>
              <MemoizedSelect
                name="dlrStatus"
                placeholder="Select Status"
                data={_pendingDlrStatusToSearch}
                onChange={_onDlrStatusToSearchInputChange}
                options={Object.React.App.enums.SMS_STATUS.enums?.map(
                  (i) => i.key,
                )}
                w="100%"
              />
            </Box>

            <Box flex="1" minW="200px">
              <Text
                mb={2}
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
              >
                Vendor Name
              </Text>
              <MemoizedInput
                placeholder="Search Vendor"
                className="filter searchInput"
                value={_pendingVendorNameToSearch}
                onChange={_onVendorNameToSearchInputChange}
              />
            </Box>

            <Box flex="1" minW="150px">
              <Text
                mb={2}
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
              >
                Sender ID
              </Text>
              <MemoizedInput
                placeholder='e.g. "SENDER_ID"'
                className="filter searchInput"
                defaultValue={_pendingSenderIdToSearch}
                onChange={_onSenderIdToSearchInputChange}
              />
            </Box>

            <Box flex="1" minW="200px">
              <Text
                mb={2}
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
              >
                SMS ID
              </Text>
              <MemoizedInput
                placeholder="Search"
                className="filter searchInput"
                defaultValue={_pendingSmsIdToSearch}
                onChange={_onSmsIdToSearchInputChange}
              />
            </Box>

            <Box flex="1" minW="200px">
              <Text
                mb={2}
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
              >
                Vendor SMS ID
              </Text>
              <MemoizedInput
                placeholder="Search"
                className="filter searchInput"
                defaultValue={_pendingVendorSmsIdToSearch}
                onChange={_onVendorSmsIdToSearchInputChange}
              />
            </Box>

            {/* Apply Filters Button */}
            <Box flex="1" minW="150px" display="flex" alignItems="flex-end">
              <Button
                w="100%"
                borderRadius="12px"
                backgroundColor="purple.200"
                color="purple"
                onClick={_handleApplyFilters}
                isDisabled={_isLoading}
                _hover={{ bg: "purple.800", color: "white" }}
              >
                Apply Filters
              </Button>
            </Box>
          </Flex>

          {/* Table*/}

          <Box
            w="100%"
            height="calc(100vh - 150px)"
            minH="520px"
            flexShrink={0}
            overflow="hidden"
          >
            <DataGrid
              onPaginationModelChange={_handlePaginationModelChange}
              rows={rows}
              columns={columns}
              rowHeight={45}
              rowCount={rowCount}
              paginationMode="server"
              filterMode="server"
              filterModel={filter}
              paginationModel={paginationModel}
              onFilterModelChange={_setFilterDebounced}
              pageSizeOptions={[30, 50, 80, 100]}
              sx={
                _isCurrentViewMobile
                  ? {
                      "& .MuiTablePagination-selectLabel": {
                        display: "block",
                      },
                      "& .MuiTablePagination-input": {
                        display: "inline-flex",
                      },
                    }
                  : void 0
              }
              slots={{
                loadingOverlay: () => (
                  <TableSpinner
                    isLoading={true}
                    isEmpty={false}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
                noResultsOverlay: () => (
                  <TableSpinner
                    isLoading={_isLoading}
                    isEmpty={!_isLoading}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
                noRowsOverlay: () => (
                  <TableSpinner
                    isLoading={_isLoading}
                    isEmpty={!_isLoading}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
              }}
              loading={_isLoading}
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={_handleColumnVisibilityModelChange}
            />
          </Box>
        </Flex>

        <Modal
          size="md"
          title="Download Report"
          isOpen={isEmailReportModalOpen}
          isCentered={true}
          onClose={onEmailReportModalClose}
        >
          <EmailReportModal
            onSubmit={_handleDownloadReport}
            isLoading={MutationReportCreateResponse.loading}
          />
        </Modal>

        <Modal
          title="PDU Report"
          isOpen={isReactJsonViewOpen}
          onClose={onReactJsonViewClose}
        >
          <Box maxH="80vh" overflowY="auto">
            <ReactJsonView src={JSON.parse(pduToShow)} enableClipboard={true} />
          </Box>
        </Modal>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {
  vendorId: PropTypes.object,
  passOn: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  activeCustomer: PropTypes.object,
};

/*
 * REDUX
 */
const _MapStateToProps = (__state) => ({
  passOn: __state.PassOn,
  activeCustomer: __state.ActiveCustomer,
});

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(React.memo(Index));
